import type { PDFDocument as PdfLibDocument } from 'pdf-lib';
import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import { Questionnaire, QuestionnaireResponse } from '../../types/fhir';
import { QuestionnaireKind } from '../../types/QuestionnaireKind';
import {
    buildResponsePdfDefinition,
    collectResponsePdfImagePaths,
    COLORS,
    formatDateTime,
    getKindLabel,
    getPatientIdentity,
    getResponsePdfTitle,
    PAGE_MARGIN_BOTTOM,
    PAGE_MARGIN_TOP,
    PAGE_MARGIN_X,
    ResponsePdfImages,
    ResponsePdfInput,
    ResponsePdfTask,
    Translate,
} from './responsePdfDocument';
import { loadResponsePdfImages, renderPdfBytes } from './renderPdf';
import { buildCombinedPdfFileName } from './pdfFileName';

export const MAX_COMBINED_PDF_RESPONSES = 50;
const MAX_PARALLEL_DOWNLOADS = 3;

export type CombinedPdfEntry = {
    task: ResponsePdfTask & { questionnaireId?: string };
    // Null when the task has no saved response.
    responseId: string | null;
};

type RenderedSection = {
    input: ResponsePdfInput;
    bytes: Uint8Array;
};

type FailedEntry = {
    entry: CombinedPdfEntry;
    reason: string;
};

type SectionPages = {
    section: RenderedSection;
    firstPage: number;
    lastPage: number;
};

const mapWithConcurrency = async <T, R>(items: T[], limit: number, map: (item: T) => Promise<R>): Promise<R[]> => {
    const results: R[] = new Array(items.length);
    let nextIndex = 0;
    const worker = async (): Promise<void> => {
        while (nextIndex < items.length) {
            const index = nextIndex++;
            results[index] = await map(items[index]);
        }
    };
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
    return results;
};

const getSortKey = (section: RenderedSection): string => {
    const patient = getPatientIdentity(section.input.task, section.input.questionnaireResponse);
    return (patient.name || patient.id || '').toLocaleLowerCase();
};

const compareSections = (a: RenderedSection, b: RenderedSection): number =>
    getSortKey(a).localeCompare(getSortKey(b)) ||
    (a.input.questionnaireResponse.authored ?? '').localeCompare(b.input.questionnaireResponse.authored ?? '');

const buildCoverDefinition = ({
    sections,
    failed,
    kind,
    t,
    locale,
    exportedAt,
}: {
    sections: SectionPages[];
    failed: FailedEntry[];
    kind: QuestionnaireKind;
    t: Translate;
    locale: string;
    exportedAt: Date;
}): TDocumentDefinitions => {
    const kindLabel = getKindLabel(kind, t);
    const headerRow = ['#', t('Patient'), t('User ID'), kindLabel, t('Submitted'), t('Pages')].map((text) => ({
        text,
        style: 'tableHeader',
    }));
    const sectionRows = sections.map(({ section, firstPage, lastPage }, index) => {
        const patient = getPatientIdentity(section.input.task, section.input.questionnaireResponse);
        return [
            String(index + 1),
            patient.name || '–',
            patient.id || '–',
            getResponsePdfTitle(section.input) || '–',
            formatDateTime(section.input.questionnaireResponse.authored, locale) || '–',
            firstPage === lastPage ? String(firstPage) : `${firstPage}–${lastPage}`,
        ];
    });
    const failedRows = failed.map(({ entry, reason }) => [
        { text: '–', style: 'failed' },
        { text: entry.task.patientName || '–', style: 'failed' },
        { text: entry.task.patientId || '–', style: 'failed' },
        { text: entry.task.questionnaireName || entry.task.questionnaireId || '–', style: 'failed' },
        { text: t('Could not be exported: {{reason}}', { reason }), style: 'failed', colSpan: 2 },
        '',
    ]);
    const exportedAtLabel = formatDateTime(exportedAt.toISOString(), locale);

    const content: Content[] = [
        { text: t('Response export'), style: 'title' },
        {
            text: `${kindLabel} · ${t('{{count}} responses', { count: sections.length })} · ${t(
                'Exported on {{date}}',
                {
                    date: exportedAtLabel,
                },
            )}`,
            style: 'subtitle',
        },
        {
            table: {
                headerRows: 1,
                widths: [16, '*', 'auto', '*', 'auto', 'auto'],
                body: [headerRow, ...sectionRows, ...failedRows],
            },
            layout: 'lightHorizontalLines',
        },
    ];

    return {
        pageSize: 'A4',
        pageOrientation: 'portrait',
        pageMargins: [PAGE_MARGIN_X, PAGE_MARGIN_TOP, PAGE_MARGIN_X, PAGE_MARGIN_BOTTOM],
        info: { title: t('Response export'), subject: kindLabel, creator: 'KoALA Questionnaire Editor' },
        header: (): Content => ({
            margin: [PAGE_MARGIN_X, 30, PAGE_MARGIN_X, 0],
            text: `KoALA · ${kindLabel}`,
            style: 'pageHeader',
        }),
        content,
        defaultStyle: { font: 'Roboto', fontSize: 9, lineHeight: 1.2, color: COLORS.text },
        styles: {
            title: { fontSize: 18, bold: true, color: COLORS.blue, margin: [0, 0, 0, 2] },
            subtitle: { fontSize: 10, color: COLORS.muted, margin: [0, 0, 0, 14] },
            tableHeader: { bold: true, color: COLORS.blue },
            failed: { color: '#b42318' },
            pageHeader: { fontSize: 8, color: COLORS.muted },
        },
    };
};

// Every response is rendered as its own PDF, so each keeps its patient in the page header and
// its own page numbers, and the PDFs are then merged behind an overview page.
export const createCombinedResponsesPdf = async ({
    entries,
    kind,
    loadResponse,
    loadQuestionnaire,
    t,
    locale,
    exportedAt,
    onProgress,
}: {
    entries: CombinedPdfEntry[];
    kind: QuestionnaireKind;
    loadResponse: (responseId: string) => Promise<QuestionnaireResponse>;
    loadQuestionnaire: (questionnaireId: string) => Promise<Questionnaire>;
    t: Translate;
    locale: string;
    exportedAt: Date;
    onProgress?: (done: number, total: number) => void;
}): Promise<{ blob: Blob; filename: string; failedCount: number }> => {
    const questionnaires = new Map<string, Promise<Questionnaire | null>>();
    const images: ResponsePdfImages = new Map();
    const failed: FailedEntry[] = [];
    let done = 0;

    // A questionnaire that cannot be loaded still allows exporting the answers alone.
    const getQuestionnaire = (questionnaireId?: string): Promise<Questionnaire | null> => {
        if (!questionnaireId) {
            return Promise.resolve(null);
        }
        if (!questionnaires.has(questionnaireId)) {
            questionnaires.set(
                questionnaireId,
                loadQuestionnaire(questionnaireId).catch(() => null),
            );
        }
        return questionnaires.get(questionnaireId) as Promise<Questionnaire | null>;
    };

    const rendered = await mapWithConcurrency(
        entries,
        MAX_PARALLEL_DOWNLOADS,
        async (entry): Promise<RenderedSection | null> => {
            try {
                if (!entry.responseId) {
                    throw new Error(t('No response found for this task.'));
                }
                const [questionnaireResponse, questionnaire] = await Promise.all([
                    loadResponse(entry.responseId),
                    getQuestionnaire(entry.task.questionnaireId),
                ]);
                const input: ResponsePdfInput = {
                    task: entry.task,
                    kind,
                    questionnaire,
                    questionnaireResponse,
                    t,
                    locale,
                    exportedAt,
                };
                await loadResponsePdfImages(collectResponsePdfImagePaths(input), images);
                return { input, bytes: await renderPdfBytes(buildResponsePdfDefinition(input, images)) };
            } catch (error) {
                failed.push({ entry, reason: error instanceof Error ? error.message : t('Unknown error') });
                return null;
            } finally {
                done += 1;
                onProgress?.(done, entries.length);
            }
        },
    );

    const sections = rendered.filter((section): section is RenderedSection => section !== null).sort(compareSections);
    if (sections.length === 0) {
        throw new Error(failed[0]?.reason || t('No response found for this task.'));
    }

    const { PDFDocument } = await import('pdf-lib');
    const sectionDocuments = await Promise.all(sections.map((section) => PDFDocument.load(section.bytes)));

    // The page ranges on the cover depend on how many pages the cover itself has.
    let coverDocument: PdfLibDocument | undefined;
    let coverPageCount = 1;
    for (let attempt = 0; attempt < 3; attempt += 1) {
        let nextPage = coverPageCount + 1;
        const sectionPages = sections.map((section, index): SectionPages => {
            const firstPage = nextPage;
            nextPage += sectionDocuments[index].getPageCount();
            return { section, firstPage, lastPage: nextPage - 1 };
        });
        coverDocument = await PDFDocument.load(
            await renderPdfBytes(buildCoverDefinition({ sections: sectionPages, failed, kind, t, locale, exportedAt })),
        );
        if (coverDocument.getPageCount() === coverPageCount) {
            break;
        }
        coverPageCount = coverDocument.getPageCount();
    }

    const merged = await PDFDocument.create();
    merged.setTitle(t('Response export'));
    merged.setCreator('KoALA Questionnaire Editor');
    for (const document of [coverDocument, ...sectionDocuments]) {
        if (!document) {
            continue;
        }
        const pages = await merged.copyPages(document, document.getPageIndices());
        pages.forEach((page) => merged.addPage(page));
    }

    return {
        blob: new Blob([await merged.save()], { type: 'application/pdf' }),
        filename: buildCombinedPdfFileName(sections.length, exportedAt),
        failedCount: failed.length,
    };
};
